import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AnimatedNumber, StatBox } from '@feathr/components';
import GrowthCreditIcon from '@feathr/extender/components/GrowthCreditIcon/GrowthCreditIcon';

import type { IFundraisingSummaryMetrics } from './FundraisingSummaryMetrics.hooks';
import useFundraisingSummaryMetrics from './FundraisingSummaryMetrics.hooks';

import * as styles from './FundraisingSummaryMetrics.css';

/**
 * Presentational component for displaying fundraising summary metrics.
 * Exported for reuse in Storybook to eliminate code duplication.
 */
export function FundraisingSummaryMetricsView({
  donationTotal,
  growthCredits,
  isLoading,
}: Readonly<IFundraisingSummaryMetrics>): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={styles.root}>
      <StatBox
        isLoading={isLoading}
        label={t('Total Donation Volume')}
        name={'donation-total'}
        primary={
          <AnimatedNumber format={{ style: 'currency', currency: 'USD' }} value={donationTotal} />
        }
      />
      <StatBox
        isLoading={isLoading}
        label={t('Total Growth Credits Earned')}
        name={'growth-credits'}
        primary={
          <span className={styles.creditsValue}>
            <AnimatedNumber value={growthCredits} />
            <span className={styles.coinIcon}>
              <GrowthCreditIcon />
            </span>
          </span>
        }
      />
    </div>
  );
}

/**
 * Container component that fetches data and renders the view.
 */
function FundraisingSummaryMetrics(): JSX.Element {
  const metrics = useFundraisingSummaryMetrics();
  return <FundraisingSummaryMetricsView {...metrics} />;
}

export default observer(FundraisingSummaryMetrics);
